import { Component, ElementRef, forwardRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-money-v2',
  templateUrl: './money-v2.component.html',
  styleUrls: ['./money-v2.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MoneyV2Component),
      multi: true,
    },
  ],
  standalone: false,
})
export class MoneyV2Component implements ControlValueAccessor {
  value: string; // stores money in internal format, like 'GBP 1000000.00'
  amount: string; // stores amount in pretty format, like '1,000,000'
  currencyCode: string; // stores currency code, like 'GBP'
  disabled: boolean;
  formatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  _moneyInput: ElementRef;

  @ViewChild('moneyInput') set moneyInput(moneyInput: ElementRef) {
    moneyInput.nativeElement.value = this.amount;
    this._moneyInput = moneyInput;
  }

  propagateChange = (_: any) => {}; // method that updates parent reactive form

  /*
  invoked by angular to set function updating reactive form
   */
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {}

  // invoked by angular on component creation with formGroupName attribute set
  writeValue(value?: string): void {
    const theValue = value || '??? 0.00';
    const splitted = theValue.split(' ');
    this.currencyCode = splitted[0];
    this.doWriteValue(splitted[1]);
  }

  // invoked by angular if field in reactive form changes isDisabled state
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  update(updatedValue: string): void {
    let caretPosition = this._moneyInput.nativeElement.selectionStart;
    if (this.doWriteValue(this.removeThousandsComaSeparators(updatedValue))) {
      this.propagateChange(this.value);
      this.setCaret(caretPosition, updatedValue);
    } else {
      this.setCaret(caretPosition - 1, updatedValue);
    }
  }

  // on each input we need to format number - that can add or remove some comas. Here we make sure cursor is in correct place
  private setCaret(caretPosition: number, beforeChanges: string): void {
    let theCaretPosition =
      caretPosition +
      this.calculateNumberOfComas(this.amount.substring(0, caretPosition)) -
      this.calculateNumberOfComas(beforeChanges.substring(0, caretPosition));
    this._moneyInput.nativeElement.setSelectionRange(theCaretPosition, theCaretPosition);
  }

  private calculateNumberOfComas(str: string) {
    return (str.match(/,/g) || []).length;
  }

  private doWriteValue(newAmount: string): boolean {
    const theNewAmount = Number(newAmount);
    if (!isNaN(theNewAmount)) {
      this.amount = this.formatter.format(theNewAmount);
      this.value = this.currencyCode + ' ' + newAmount;
      this.updateInput();
      return true;
    } else {
      this.updateInput();
      return false;
    }
  }

  private updateInput(): void {
    if (this._moneyInput) {
      this._moneyInput.nativeElement.value = this.amount;
    }
  }

  private removeThousandsComaSeparators($event: string) {
    // @ts-ignore
    return $event.replaceAll(',', '');
  }

  onInputChange($event: Event) {
    // @ts-ignore
    this.update($event.target.value);
  }
}
